import { useEffect, useMemo, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon, Calculator, Plus, Trash, FloppyDisk, DownloadSimple,
  CheckCircle, Archive, UploadSimple, FileCsv,
} from "@phosphor-icons/react";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const STATUSES = [
  { v: "PRESENT", label: "Present", color: "text-[#22C55E]" },
  { v: "ABSENT", label: "Absent", color: "text-[#FF3B30]" },
  { v: "HALF_DAY", label: "Half Day", color: "text-[#F59E0B]" },
  { v: "PAID_LEAVE", label: "Paid Leave", color: "text-zinc-300" },
  { v: "EMERGENCY_LEAVE", label: "Emergency Leave", color: "text-zinc-300" },
  { v: "MEDICAL_LEAVE", label: "Medical Leave", color: "text-zinc-300" },
  { v: "WELLNESS_LEAVE", label: "Wellness Leave", color: "text-zinc-300" },
  { v: "HOLIDAY", label: "Holiday", color: "text-zinc-400" },
  { v: "FESTIVAL_HOLIDAY", label: "Festival Holiday", color: "text-zinc-400" },
  { v: "SPECIAL_HOLIDAY", label: "Special Holiday", color: "text-zinc-400" },
  { v: "WEEKLY_OFF", label: "Weekly Off", color: "text-zinc-500" },
  { v: "UNPAID_LEAVE", label: "Unpaid Leave", color: "text-[#FF3B30]" },
];

const DEDUCTION_CATS = [
  "SALARY_ADVANCE", "EMPLOYEE_LOAN", "TRAFFIC_FINE", "VEHICLE_FINE",
  "OFFICE_FINE", "CUSTOMER_COMPLAINT", "UNIFORM_FINE", "ID_CARD_FINE",
  "ASSET_DAMAGE", "SAFETY_VIOLATION", "MOBILE_DAMAGE", "CUSTOM",
];
const BONUS_TYPES = [
  "ATTENDANCE_BONUS", "PERFORMANCE_BONUS", "COLLECTION_INCENTIVE",
  "SALES_INCENTIVE", "REFERRAL_BONUS", "FESTIVAL_BONUS",
  "MANAGEMENT_REWARD", "SPECIAL_REWARD", "CUSTOM_INCENTIVE",
];

function computeHours(time_in, time_out, break_hours) {
  if (!time_in || !time_out) return 0;
  const [h1, m1] = time_in.split(":").map(Number);
  const [h2, m2] = time_out.split(":").map(Number);
  if (Number.isNaN(h1) || Number.isNaN(h2)) return 0;
  let diff = (h2 * 60 + m2) - (h1 * 60 + m1);
  if (diff < 0) diff += 24 * 60;
  return Math.max(0, +(diff / 60 - (break_hours || 0)).toFixed(2));
}

const CSV_HEADERS = ["Date","Day","Status","TimeIn","TimeOut","BreakHours","WorkingHours","OvertimeHours","JobsAssigned","JobsCompleted","Remarks"];
const VALID_STATUSES = new Set(STATUSES.map((s) => s.v));

function csvEscape(v) {
  if (v == null) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s;
}

function daysToCsv(days, employee, month, year) {
  const lines = [];
  lines.push(`# Rescigo WMS - Manual Payroll Template`);
  lines.push(`# Employee: ${employee?.name || ""} (${employee?.employee_id || ""})`);
  lines.push(`# Period: ${String(month).padStart(2,"0")}/${year}`);
  lines.push(`# Allowed Status values: ${[...VALID_STATUSES].join(" | ")}`);
  lines.push(`# Times in 24h HH:MM. BreakHours/OvertimeHours are decimals (e.g. 1.5). WorkingHours auto-recomputed on upload.`);
  lines.push(CSV_HEADERS.join(","));
  for (const d of days) {
    const wd = new Date(d.date).toLocaleDateString(undefined, { weekday: "short" });
    lines.push([
      d.date, wd, d.status, d.time_in || "", d.time_out || "",
      d.break_hours || 0, d.working_hours || 0, d.overtime_hours || 0,
      d.jobs_assigned || 0, d.jobs_completed || 0,
      csvEscape(d.remarks || ""),
    ].join(","));
  }
  return lines.join("\n");
}

function parseCsv(text) {
  // Simple CSV parser supporting quoted fields with embedded commas/quotes
  const rows = [];
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  for (const raw of lines) {
    const line = raw.trim();
    if (!line || line.startsWith("#")) continue;
    const cells = [];
    let cur = "", inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQ) {
        if (ch === '"' && line[i+1] === '"') { cur += '"'; i++; }
        else if (ch === '"') { inQ = false; }
        else cur += ch;
      } else {
        if (ch === '"') inQ = true;
        else if (ch === ",") { cells.push(cur); cur = ""; }
        else cur += ch;
      }
    }
    cells.push(cur);
    rows.push(cells.map((c) => c.trim()));
  }
  return rows;
}

function csvToDays(text, currentDays) {
  const rows = parseCsv(text);
  if (!rows.length) throw new Error("Empty CSV");
  const header = rows[0].map((h) => h.toLowerCase());
  const idx = Object.fromEntries(CSV_HEADERS.map((h) => [h, header.indexOf(h.toLowerCase())]));
  if (idx.Date === -1 || idx.Status === -1) throw new Error("Missing required columns: Date, Status");
  const byDate = Object.fromEntries(currentDays.map((d) => [d.date, d]));
  const errors = [];
  for (let i = 1; i < rows.length; i++) {
    const r = rows[i];
    const date = r[idx.Date];
    if (!date) continue;
    if (!byDate[date]) { errors.push(`Row ${i+1}: date ${date} not in calendar`); continue; }
    const status = (r[idx.Status] || "").toUpperCase();
    if (status && !VALID_STATUSES.has(status)) { errors.push(`Row ${i+1}: invalid status "${status}"`); continue; }
    const time_in  = idx.TimeIn  >= 0 ? (r[idx.TimeIn]  || "") : "";
    const time_out = idx.TimeOut >= 0 ? (r[idx.TimeOut] || "") : "";
    const break_h  = idx.BreakHours    >= 0 ? Number(r[idx.BreakHours] || 0) || 0 : 0;
    const ot_h     = idx.OvertimeHours >= 0 ? Number(r[idx.OvertimeHours] || 0) || 0 : 0;
    const jobs_a   = idx.JobsAssigned  >= 0 ? Number(r[idx.JobsAssigned]  || 0) || 0 : 0;
    const jobs_c   = idx.JobsCompleted >= 0 ? Number(r[idx.JobsCompleted] || 0) || 0 : 0;
    const remarks  = idx.Remarks       >= 0 ? (r[idx.Remarks] || "") : "";
    const wh = computeHours(time_in, time_out, break_h);
    byDate[date] = {
      ...byDate[date],
      status: status || byDate[date].status,
      time_in, time_out, break_hours: break_h, overtime_hours: ot_h,
      working_hours: wh, remarks,
      jobs_assigned: jobs_a, jobs_completed: jobs_c,
    };
  }
  return { days: Object.values(byDate), errors };
}

export default function ManualPayroll() {
  const { user } = useAuth();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [employees, setEmployees] = useState([]);
  const [empId, setEmpId] = useState("");
  const [days, setDays] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [otRate, setOtRate] = useState("");
  // Productivity settings (legacy — kept for back-compat)
  const [enableProductivity, setEnableProductivity] = useState(false);
  const [minJobs, setMinJobs] = useState(0);
  const [incRate, setIncRate] = useState(0);
  // Phase 7: Salary calc method
  const [salaryCalcMethod, setSalaryCalcMethod] = useState("FULL_DAY");
  // Phase 7: Performance bonus / deduction module
  const [enablePerformance, setEnablePerformance] = useState(false);
  const [dailyAssignedJobs, setDailyAssignedJobs] = useState(0);
  const [minRequiredJobs, setMinRequiredJobs] = useState(0);
  const [perfDedRate, setPerfDedRate] = useState(0);
  const [perfBonRate, setPerfBonRate] = useState(0);
  const [calc, setCalc] = useState(null);
  const [slips, setSlips] = useState([]);
  const [archivedSlips, setArchivedSlips] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [currentStatus, setCurrentStatus] = useState("DRAFT");
  const [showDed, setShowDed] = useState(false);
  const [showBon, setShowBon] = useState(false);
  const [dedForm, setDedForm] = useState({ category: "CUSTOM", reason: "", amount: 0, remarks: "" });
  const [bonForm, setBonForm] = useState({ bonus_type: "PERFORMANCE_BONUS", reason: "", amount: 0, remarks: "" });
  const [tab, setTab] = useState("calc");

  useEffect(() => { api.get("/employees").then((r) => setEmployees(r.data.filter((e) => e.active))); }, []);
  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, [month, year]);

  async function loadList() {
    try {
      const a = await api.get(`/manual-payroll?month=${month}&year=${year}&archived=false`);
      setSlips(a.data);
      const b = await api.get(`/manual-payroll?month=${month}&year=${year}&archived=true`);
      setArchivedSlips(b.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function loadCalendar() {
    if (!empId) { toast.error("Pick an employee first"); return; }
    try {
      // load existing draft if any
      const list = await api.get(`/manual-payroll?month=${month}&year=${year}&employee_id=${empId}`);
      const existing = list.data[0];
      if (existing) {
        loadSlip(existing);
        toast.success(`Loaded existing ${existing.status} payslip`);
      } else {
        const r = await api.get(`/manual-payroll/calendar?month=${month}&year=${year}&employee_id=${empId}`);
        setDays(r.data.days);
        setDeductions([]); setBonuses([]); setOtRate(""); setCalc(null);
        setEnableProductivity(false); setMinJobs(0); setIncRate(0);
        // Apply employee-level defaults for the new payroll method + perf settings
        const e = employees.find((x) => x.id === empId);
        if (e) {
          setSalaryCalcMethod(e.salary_calc_method || "FULL_DAY");
          setDailyAssignedJobs(e.daily_assigned_jobs || 0);
          setMinRequiredJobs(e.min_required_jobs || 0);
          setPerfDedRate(e.perf_deduction_per_missing_job || 0);
          setPerfBonRate(e.perf_bonus_per_extra_job || 0);
          setEnablePerformance(
            !!(e.daily_assigned_jobs || e.min_required_jobs ||
               e.perf_deduction_per_missing_job || e.perf_bonus_per_extra_job)
          );
        } else {
          setSalaryCalcMethod("FULL_DAY"); setEnablePerformance(false);
          setDailyAssignedJobs(0); setMinRequiredJobs(0);
          setPerfDedRate(0); setPerfBonRate(0);
        }
        setCurrentId(null); setCurrentStatus("DRAFT");
        toast.success("Calendar loaded");
      }
    } catch (e) { toast.error(formatApiError(e)); }
  }

  // Hydrate calculator from a saved slip (used by list "Open" action — avoids stale state)
  function loadSlip(s) {
    setEmpId(s.employee_id);
    setMonth(s.month);
    setYear(s.year);
    setDays(s.days || []);
    setDeductions(s.deductions || []);
    setBonuses(s.bonuses || []);
    setOtRate(s.overtime_rate_per_hour ?? "");
    setEnableProductivity(!!s.enable_productivity);
    setMinJobs(s.min_jobs_per_day || 0);
    setIncRate(s.incentive_per_extra_job || 0);
    setSalaryCalcMethod(s.salary_calc_method || "FULL_DAY");
    setEnablePerformance(!!s.enable_performance);
    setDailyAssignedJobs(s.daily_assigned_jobs || 0);
    setMinRequiredJobs(s.min_required_jobs || 0);
    setPerfDedRate(s.perf_deduction_per_missing_job || 0);
    setPerfBonRate(s.perf_bonus_per_extra_job || 0);
    setCurrentId(s.id);
    setCurrentStatus(s.status);
    setCalc(s.calculation || null);
    setTab("calc");
  }

  function updateDay(idx, patch) {
    setDays((arr) => {
      const next = [...arr];
      const merged = { ...next[idx], ...patch };
      // auto-recompute working_hours when times change
      if ("time_in" in patch || "time_out" in patch || "break_hours" in patch) {
        merged.working_hours = computeHours(merged.time_in, merged.time_out, merged.break_hours);
      }
      next[idx] = merged;
      return next;
    });
  }

  function bulkSet(weekday, status) {
    setDays((arr) => arr.map((d) => {
      const dt = new Date(d.date);
      return dt.getDay() === weekday ? { ...d, status } : d;
    }));
  }

  function buildPayload() {
    return {
      employee_id: empId, month, year, days, deductions, bonuses,
      overtime_rate_per_hour: otRate === "" ? null : Number(otRate),
      salary_calc_method: salaryCalcMethod,
      enable_productivity: enableProductivity,
      min_jobs_per_day: Number(minJobs) || 0,
      incentive_per_extra_job: Number(incRate) || 0,
      enable_performance: enablePerformance,
      daily_assigned_jobs: Number(dailyAssignedJobs) || 0,
      min_required_jobs: Number(minRequiredJobs) || 0,
      perf_deduction_per_missing_job: Number(perfDedRate) || 0,
      perf_bonus_per_extra_job: Number(perfBonRate) || 0,
    };
  }

  async function preview() {
    if (!empId || !days.length) { toast.error("Load calendar first"); return; }
    try {
      const { data } = await api.post("/manual-payroll/preview", buildPayload());
      setCalc(data.calculation);
      // Sync normalized day state (auto-absent etc.) back to UI for visibility
      if (data.calculation?.normalized_days) {
        setDays(data.calculation.normalized_days);
      }
      toast.success(`Net: ₹ ${data.calculation.net_salary.toLocaleString()}`);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function saveDraft() {
    if (!empId) return toast.error("Pick an employee");
    try {
      const body = { id: currentId, ...buildPayload() };
      const { data } = await api.post("/manual-payroll", body);
      setCurrentId(data.id); setCurrentStatus(data.status); setCalc(data.calculation);
      if (data.calculation?.normalized_days) setDays(data.calculation.normalized_days);
      toast.success("Saved as draft");
      loadList();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function changeStatus(status) {
    if (!currentId) { toast.error("Save first"); return; }
    try {
      const { data } = await api.patch(`/manual-payroll/${currentId}/status`, { status });
      setCurrentStatus(data.status);
      toast.success(`Marked ${status}`);
      loadList();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function archiveCurrent() {
    if (!currentId) return;
    if (!window.confirm("Archive this payslip? It can be restored later.")) return;
    try {
      await api.delete(`/manual-payroll/${currentId}`);
      toast.success("Archived");
      setCurrentId(null); setCurrentStatus("DRAFT"); setDays([]); setCalc(null);
      loadList();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function restore(id) {
    try { await api.post(`/manual-payroll/${id}/restore`); toast.success("Restored"); loadList(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  // When user prints a slip from the open calculator (DRAFT), auto-save first so
  // any unsaved changes (salary method, perf settings, days) are reflected in the PDF.
  async function downloadPDF(id) {
    // If this is the currently-open slip and it's a DRAFT, save first so the
    // PDF includes the latest UI state.
    if (id && id === currentId && currentStatus === "DRAFT" && empId && days.length) {
      try {
        const body = { id: currentId, ...buildPayload() };
        const { data } = await api.post("/manual-payroll", body);
        setCurrentId(data.id); setCurrentStatus(data.status); setCalc(data.calculation);
        if (data.calculation?.normalized_days) setDays(data.calculation.normalized_days);
        loadList();
      } catch (e) {
        toast.error(`Could not save before printing: ${formatApiError(e)}`);
        return;
      }
    }
    window.open(`${API}/manual-payroll/${id}/pdf`, "_blank");
  }

  async function downloadDetailedPDF(id) {
    if (id && id === currentId && currentStatus === "DRAFT" && empId && days.length) {
      try {
        const body = { id: currentId, ...buildPayload() };
        const { data } = await api.post("/manual-payroll", body);
        setCurrentId(data.id); setCurrentStatus(data.status); setCalc(data.calculation);
        if (data.calculation?.normalized_days) setDays(data.calculation.normalized_days);
        loadList();
      } catch (e) {
        toast.error(`Could not save before printing: ${formatApiError(e)}`);
        return;
      }
    }
    window.open(`${API}/manual-payroll/${id}/detailed-pdf`, "_blank");
  }

  // Live validation: count days marked PRESENT/HALF_DAY without times
  const invalidDayCount = useMemo(() =>
    days.filter((d) => (d.status === "PRESENT" || d.status === "HALF_DAY")
                       && (!d.time_in || !d.time_out)).length, [days]);

  function downloadTemplate() {
    if (!days.length) { toast.error("Load calendar first"); return; }
    const csv = daysToCsv(days, selectedEmp, month, year);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const code = selectedEmp?.employee_id || "EMP";
    a.href = url;
    a.download = `manual-payroll-${code}-${String(month).padStart(2,"0")}-${year}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV template downloaded");
  }

  async function uploadCsv(file) {
    if (!file) return;
    if (!days.length) { toast.error("Load calendar first"); return; }
    try {
      const text = await file.text();
      const { days: next, errors } = csvToDays(text, days);
      setDays(next);
      if (errors.length) {
        toast.warning(`${errors.length} row(s) skipped — see console`);
        // eslint-disable-next-line no-console
        console.warn("[ManualPayroll CSV import]", errors);
      } else {
        toast.success(`Imported ${next.length} days from CSV`);
      }
    } catch (e) {
      toast.error(e.message || "Failed to parse CSV");
    }
  }

  const selectedEmp = useMemo(() => employees.find((e) => e.id === empId), [empId, employees]);

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#E50914] mb-2">Engine</p>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight">Manual Payroll Calculator</h1>
          <p className="text-xs text-zinc-500 mt-1">Calendar-based attendance entry · works without GPS attendance · live calc</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#16161C] border border-[#2D2D35]">
          <TabsTrigger value="calc" data-testid="mp-tab-calc">Calculator</TabsTrigger>
          <TabsTrigger value="list" data-testid="mp-tab-list">This Month ({slips.length})</TabsTrigger>
          <TabsTrigger value="archive" data-testid="mp-tab-archive">Archive ({archivedSlips.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="calc" className="mt-5">
          {/* Step 1 / 2 */}
          <div className="bg-[#16161C] border border-[#2D2D35] rounded-md p-5 grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Employee</Label>
              <Select value={empId} onValueChange={setEmpId}>
                <SelectTrigger className="mt-2 bg-[#0D0D10] border-[#2D2D35]" data-testid="mp-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white max-h-72">
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.employee_id}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Month</Label>
              <Select value={String(month)} onValueChange={(v)=>setMonth(Number(v))}>
                <SelectTrigger className="mt-2 bg-[#0D0D10] border-[#2D2D35]" data-testid="mp-month-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white">
                  {MONTHS.map((m, i) => <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Year</Label>
              <Select value={String(year)} onValueChange={(v)=>setYear(Number(v))}>
                <SelectTrigger className="mt-2 bg-[#0D0D10] border-[#2D2D35]" data-testid="mp-year-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white">
                  {[year-1, year, year+1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-4 flex items-center gap-3">
              <Button onClick={loadCalendar} className="bg-[#E50914] hover:bg-[#C80812]" data-testid="mp-load-calendar">
                <CalendarIcon size={16} className="mr-1" /> Load Calendar
              </Button>
              {selectedEmp && (
                <div className="text-xs text-zinc-400 font-mono">
                  Monthly ₹ {Number(selectedEmp.monthly_salary||0).toLocaleString()} · {selectedEmp.monthly_working_days || 26}d/{selectedEmp.daily_working_hours || 8}h
                  · Daily ₹ {(selectedEmp.daily_rate || 0).toLocaleString()} · Hourly ₹ {(selectedEmp.hourly_rate || 0).toLocaleString()}
                </div>
              )}
              {currentId && (
                <span className="ml-auto text-xs uppercase tracking-widest px-2 py-1 rounded bg-[#0D0D10] border border-[#2D2D35]">
                  Status: <span className="text-[#E50914]">{currentStatus}</span>
                </span>
              )}
            </div>
          </div>

          {days.length > 0 && (<>
            {/* Salary Calculation Method (Phase 7) */}
            <div className="mt-4 bg-[#16161C] border border-[#2D2D35] rounded-md p-4" data-testid="mp-method-card">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#E50914]">Salary Calculation Method</p>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {salaryCalcMethod === "WORKED_HOURS"
                      ? "Pay only for actual hours worked × hourly rate (no full-day fallback)."
                      : "Pay full day rate for Present + 0.5× for Half Day + full for Paid Leaves."}
                  </p>
                </div>
                <div className="flex gap-2" data-testid="mp-method-toggle">
                  <button type="button" onClick={()=>setSalaryCalcMethod("FULL_DAY")}
                          data-testid="mp-method-fullday"
                          className={`px-3 h-9 text-xs uppercase tracking-widest rounded border ${
                            salaryCalcMethod === "FULL_DAY"
                              ? "bg-[#E50914] border-[#E50914] text-white"
                              : "border-[#2D2D35] text-zinc-400 hover:border-[#E50914]"}`}>
                    Full Day Salary
                  </button>
                  <button type="button" onClick={()=>setSalaryCalcMethod("WORKED_HOURS")}
                          data-testid="mp-method-hours"
                          className={`px-3 h-9 text-xs uppercase tracking-widest rounded border ${
                            salaryCalcMethod === "WORKED_HOURS"
                              ? "bg-[#E50914] border-[#E50914] text-white"
                              : "border-[#2D2D35] text-zinc-400 hover:border-[#E50914]"}`}>
                    Worked Hours Salary
                  </button>
                </div>
              </div>
            </div>

            {/* Performance Target Settings (Phase 7) */}
            <div className="mt-4 bg-[#16161C] border border-[#2D2D35] rounded-md p-4" data-testid="mp-performance-card">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#E50914]">Performance Target Settings</p>
                  <p className="text-xs text-zinc-500 mt-0.5">Daily targets for bonus &amp; deduction based on jobs completed.</p>
                </div>
                <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none" data-testid="mp-performance-toggle">
                  <input type="checkbox" checked={enablePerformance}
                         onChange={(e) => setEnablePerformance(e.target.checked)}
                         className="h-4 w-4 accent-[#E50914]" />
                  <span className={`uppercase tracking-widest ${enablePerformance ? "text-[#22C55E]" : "text-zinc-500"}`}>
                    {enablePerformance ? "Enabled" : "Disabled"}
                  </span>
                </label>
              </div>
              <div className={`grid grid-cols-1 md:grid-cols-4 gap-3 ${enablePerformance ? "" : "opacity-40 pointer-events-none"}`}>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Daily Assigned Jobs</Label>
                  <Input type="number" min="0" value={dailyAssignedJobs}
                         onChange={(e)=>setDailyAssignedJobs(Number(e.target.value)||0)}
                         placeholder="e.g. 30"
                         className="mt-2 bg-[#0D0D10] border-[#2D2D35]" data-testid="mp-perf-assigned" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Minimum Required (No Ded.)</Label>
                  <Input type="number" min="0" value={minRequiredJobs}
                         onChange={(e)=>setMinRequiredJobs(Number(e.target.value)||0)}
                         placeholder="e.g. 20"
                         className="mt-2 bg-[#0D0D10] border-[#2D2D35]" data-testid="mp-perf-min" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Deduction / Missing Job (₹)</Label>
                  <Input type="number" min="0" step="0.5" value={perfDedRate}
                         onChange={(e)=>setPerfDedRate(Number(e.target.value)||0)}
                         placeholder="e.g. 30"
                         className="mt-2 bg-[#0D0D10] border-[#2D2D35]" data-testid="mp-perf-ded-rate" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Bonus / Extra Job (₹)</Label>
                  <Input type="number" min="0" step="0.5" value={perfBonRate}
                         onChange={(e)=>setPerfBonRate(Number(e.target.value)||0)}
                         placeholder="e.g. 20"
                         className="mt-2 bg-[#0D0D10] border-[#2D2D35]" data-testid="mp-perf-bon-rate" />
                </div>
              </div>
            </div>

            {/* Productivity settings (legacy, additive) */}
            <div className="mt-4 bg-[#16161C] border border-[#2D2D35] rounded-md p-4" data-testid="mp-productivity-card">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#E50914]">Productivity Settings</p>
                  <p className="text-xs text-zinc-500 mt-0.5">Pay an extra incentive for jobs completed above target.</p>
                </div>
                <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none" data-testid="mp-productivity-toggle">
                  <input type="checkbox" checked={enableProductivity}
                         onChange={(e) => setEnableProductivity(e.target.checked)}
                         className="h-4 w-4 accent-[#E50914]" />
                  <span className={`uppercase tracking-widest ${enableProductivity ? "text-[#22C55E]" : "text-zinc-500"}`}>
                    {enableProductivity ? "Enabled" : "Disabled"}
                  </span>
                </label>
              </div>
              <div className={`grid grid-cols-1 md:grid-cols-3 gap-3 ${enableProductivity ? "" : "opacity-40 pointer-events-none"}`}>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Minimum Job Target / Day</Label>
                  <Input type="number" min="0" value={minJobs}
                         onChange={(e)=>setMinJobs(Number(e.target.value)||0)}
                         placeholder="e.g. 10"
                         className="mt-2 bg-[#0D0D10] border-[#2D2D35]" data-testid="mp-min-jobs" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Incentive Per Additional Job (₹)</Label>
                  <Input type="number" min="0" step="0.5" value={incRate}
                         onChange={(e)=>setIncRate(Number(e.target.value)||0)}
                         placeholder="e.g. 20"
                         className="mt-2 bg-[#0D0D10] border-[#2D2D35]" data-testid="mp-inc-rate" />
                </div>
                <div className="flex flex-col">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Effective Rate</Label>
                  <div className="mt-2 font-mono text-sm text-[#E50914]">
                    ₹ {(Number(incRate)||0).toFixed(2)} <span className="text-zinc-500">/ job above</span> {Number(minJobs)||0}
                  </div>
                </div>
              </div>
            </div>

            {/* Validation banner */}
            {invalidDayCount > 0 && (
              <div className="mt-3 p-3 rounded-md border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B] text-xs flex items-start gap-2"
                   data-testid="mp-validation-warning">
                <span className="font-semibold uppercase tracking-widest text-[10px]">⚠ Attendance validation</span>
                <span>
                  {invalidDayCount} day{invalidDayCount > 1 ? "s" : ""} marked PRESENT / HALF DAY without Time In or Time Out.
                  These will be automatically treated as <b>ABSENT</b> on Calculate — Working Hours, Overtime and Productivity Incentive set to 0.
                </span>
              </div>
            )}

            {/* CSV import / export */}
            <div className="mt-4 bg-[#16161C] border border-[#2D2D35] rounded-md p-4 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 text-xs text-zinc-400">
                <FileCsv size={16} className="text-[#E50914]" />
                <span className="uppercase tracking-widest text-[10px]">CSV</span>
                <span className="text-zinc-500">Download the template, edit in Excel/Sheets, then upload back.</span>
              </div>
              <div className="ml-auto flex gap-2">
                <Button onClick={downloadTemplate} variant="outline" data-testid="mp-csv-template"
                        className="border-[#2D2D35] bg-transparent">
                  <DownloadSimple size={14} className="mr-1" /> Download Template
                </Button>
                <label className="inline-flex items-center gap-1 cursor-pointer border border-[#E50914] bg-[#E50914] hover:bg-[#C80812] text-white px-3 h-9 text-sm rounded-md font-medium transition-colors">
                  <UploadSimple size={14} /> Upload CSV
                  <input type="file" accept=".csv,text/csv" hidden data-testid="mp-csv-upload"
                         onChange={(e) => { const f = e.target.files?.[0]; uploadCsv(f); e.target.value = ""; }} />
                </label>
              </div>
            </div>

            {/* Bulk quick set */}
            <div className="mt-4 flex flex-wrap gap-2 items-center">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Bulk</span>
              {[0,1,2,3,4,5,6].map((wd) => (
                <button key={wd} onClick={()=>bulkSet(wd, "WEEKLY_OFF")}
                        className="text-[10px] uppercase border border-[#2D2D35] px-2 py-1 rounded hover:border-[#E50914]">
                  {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][wd]} → Off
                </button>
              ))}
            </div>

            {/* Step 5/6 - Day entries */}
            <div className="mt-5 bg-[#16161C] border border-[#2D2D35] rounded-md overflow-x-auto">
              <table className="w-full min-w-[1500px]" data-testid="mp-days-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[#2D2D35]">
                    <th className="text-left p-2">Date</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">In</th>
                    <th className="text-left p-2">Out</th>
                    <th className="text-right p-2">Break</th>
                    <th className="text-right p-2">Exp h</th>
                    <th className="text-right p-2">Actual h</th>
                    <th className="text-right p-2">OT (h)</th>
                    <th className="text-right p-2">Daily ₹</th>
                    <th className="text-right p-2">Jobs A</th>
                    <th className="text-right p-2">Jobs C</th>
                    <th className="text-right p-2">Miss</th>
                    <th className="text-right p-2">Ded</th>
                    <th className="text-right p-2">Extra</th>
                    <th className="text-right p-2">Bonus</th>
                    <th className="text-left p-2">Remarks</th>
                  </tr>
                </thead>
                <tbody className="text-xs">
                  {days.map((d, i) => {
                    const wd = new Date(d.date).getDay();
                    const isOff = wd === 0;
                    const needsTimes = d.status === "PRESENT" || d.status === "HALF_DAY";
                    const invalid = needsTimes && (!d.time_in || !d.time_out);
                    const isValidPresent = needsTimes && !invalid;
                    const dwh = Number(selectedEmp?.daily_working_hours || 8);
                    const expH = d.status === "PRESENT" ? dwh
                                : d.status === "HALF_DAY" ? dwh / 2 : 0;
                    const actH = Number(d.working_hours || 0);
                    const dailyR = Number(selectedEmp?.daily_rate || 0);
                    const hR = Number(selectedEmp?.hourly_rate || 0);
                    const PAID_LEAVE = ["PAID_LEAVE","EMERGENCY_LEAVE","MEDICAL_LEAVE","WELLNESS_LEAVE"];
                    let daily$ = 0;
                    if (salaryCalcMethod === "WORKED_HOURS") {
                      daily$ = isValidPresent ? actH * hR : (PAID_LEAVE.includes(d.status) ? dailyR : 0);
                    } else {
                      daily$ = (d.status === "PRESENT" && isValidPresent) ? dailyR
                             : (d.status === "HALF_DAY" && isValidPresent) ? dailyR / 2
                             : (PAID_LEAVE.includes(d.status) ? dailyR : 0);
                    }
                    const jc = Number(d.jobs_completed || 0);
                    const perfMissing = (enablePerformance && isValidPresent && Number(minRequiredJobs) > 0)
                                        ? Math.max(0, Number(minRequiredJobs) - jc) : 0;
                    const perfDed = perfMissing * Number(perfDedRate || 0);
                    const perfExtra = (enablePerformance && isValidPresent && Number(dailyAssignedJobs) > 0)
                                       ? Math.max(0, jc - Number(dailyAssignedJobs)) : 0;
                    const perfBon = perfExtra * Number(perfBonRate || 0);
                    return (
                      <tr key={d.date}
                          className={`border-b border-[#2D2D35]/40 ${isOff ? "bg-[#0D0D10]" : ""} ${invalid ? "bg-[#F59E0B]/5" : ""}`}>
                        <td className="p-2 font-mono whitespace-nowrap">
                          {d.date.slice(8)} <span className="text-zinc-500">{new Date(d.date).toLocaleDateString(undefined,{weekday:"short"})}</span>
                          {d.auto_absent_reason && <span title={d.auto_absent_reason} className="ml-1 text-[#F59E0B]">*</span>}
                        </td>
                        <td className="p-2">
                          <Select value={d.status} onValueChange={(v)=>updateDay(i, { status: v })}>
                            <SelectTrigger className={`h-7 bg-[#0D0D10] border-[#2D2D35] text-xs w-36 ${invalid ? "border-[#F59E0B]" : ""}`}><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white">
                              {STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-2"><Input type="time" value={d.time_in||""} onChange={(e)=>updateDay(i,{time_in:e.target.value})}
                               className={`h-7 w-24 bg-[#0D0D10] border-[#2D2D35] text-xs [color-scheme:dark] ${needsTimes && !d.time_in ? "border-[#F59E0B]" : ""}`} /></td>
                        <td className="p-2"><Input type="time" value={d.time_out||""} onChange={(e)=>updateDay(i,{time_out:e.target.value})}
                               className={`h-7 w-24 bg-[#0D0D10] border-[#2D2D35] text-xs [color-scheme:dark] ${needsTimes && !d.time_out ? "border-[#F59E0B]" : ""}`} /></td>
                        <td className="p-2 text-right"><Input type="number" step="0.25" value={d.break_hours||0} onChange={(e)=>updateDay(i,{break_hours:Number(e.target.value)})}
                               className="h-7 w-14 bg-[#0D0D10] border-[#2D2D35] text-xs text-right" /></td>
                        <td className="p-2 text-right font-mono text-zinc-500">{expH || "—"}</td>
                        <td className="p-2 text-right font-mono text-zinc-300">{actH || 0}</td>
                        <td className="p-2 text-right"><Input type="number" step="0.25" value={d.overtime_hours||0} onChange={(e)=>updateDay(i,{overtime_hours:Number(e.target.value)})}
                               className="h-7 w-14 bg-[#0D0D10] border-[#2D2D35] text-xs text-right" /></td>
                        <td className="p-2 text-right font-mono text-[#E50914]">{daily$ ? `₹${Math.round(daily$).toLocaleString()}` : "—"}</td>
                        <td className="p-2 text-right"><Input type="number" min="0" value={d.jobs_assigned||0} onChange={(e)=>updateDay(i,{jobs_assigned:Number(e.target.value)||0})}
                               className="h-7 w-14 bg-[#0D0D10] border-[#2D2D35] text-xs text-right" /></td>
                        <td className="p-2 text-right"><Input type="number" min="0" value={d.jobs_completed||0} onChange={(e)=>updateDay(i,{jobs_completed:Number(e.target.value)||0})}
                               className="h-7 w-14 bg-[#0D0D10] border-[#2D2D35] text-xs text-right" /></td>
                        <td className="p-2 text-right font-mono text-[#FF3B30]">{perfMissing || "—"}</td>
                        <td className="p-2 text-right font-mono text-[#FF3B30]">{perfDed ? `-₹${perfDed.toFixed(0)}` : "—"}</td>
                        <td className="p-2 text-right font-mono text-[#22C55E]">{perfExtra || "—"}</td>
                        <td className="p-2 text-right font-mono text-[#22C55E]">{perfBon ? `+₹${perfBon.toFixed(0)}` : "—"}</td>
                        <td className="p-2"><Input value={d.remarks||""} onChange={(e)=>updateDay(i,{remarks:e.target.value})}
                               className="h-7 bg-[#0D0D10] border-[#2D2D35] text-xs" /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Deductions / Bonuses / OT rate */}
            <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Block title="Deductions" total={deductions.reduce((s,d)=>s+Number(d.amount||0),0)}
                     addLabel="Add deduction" onAdd={()=>setShowDed(true)} testid="mp-add-deduction">
                {deductions.map((d, i) => (
                  <Row key={i} left={`${d.category} · ${d.reason||""}`} right={`-₹ ${Number(d.amount).toLocaleString()}`} color="#FF3B30"
                       onRemove={()=>setDeductions(deductions.filter((_,j)=>j!==i))} />
                ))}
                {!deductions.length && <Empty>No deductions added</Empty>}
              </Block>
              <Block title="Bonuses / Incentives" total={bonuses.reduce((s,b)=>s+Number(b.amount||0),0)}
                     addLabel="Add bonus" onAdd={()=>setShowBon(true)} testid="mp-add-bonus">
                {bonuses.map((b, i) => (
                  <Row key={i} left={`${b.bonus_type} · ${b.reason||""}`} right={`+₹ ${Number(b.amount).toLocaleString()}`} color="#22C55E"
                       onRemove={()=>setBonuses(bonuses.filter((_,j)=>j!==i))} />
                ))}
                {!bonuses.length && <Empty>No bonuses added</Empty>}
              </Block>
            </div>

            <div className="mt-4 bg-[#16161C] border border-[#2D2D35] rounded-md p-4 flex flex-wrap items-end gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Overtime rate / hour (₹)</Label>
                <Input value={otRate} onChange={(e)=>setOtRate(e.target.value)} type="number"
                       placeholder="auto: 1.5× hourly" data-testid="mp-ot-rate"
                       className="mt-2 bg-[#0D0D10] border-[#2D2D35] w-48" />
              </div>
              <div className="ml-auto flex flex-wrap gap-2">
                <Button onClick={preview} variant="outline" className="border-[#2D2D35] bg-transparent" data-testid="mp-calculate">
                  <Calculator size={16} className="mr-1" /> Calculate
                </Button>
                <Button onClick={saveDraft} className="bg-[#E50914] hover:bg-[#C80812]" data-testid="mp-save-draft">
                  <FloppyDisk size={16} className="mr-1" /> Save Draft
                </Button>
              </div>
            </div>

            {/* Final salary builder */}
            {calc && <FinalBuilder calc={calc} deductions={deductions} bonuses={bonuses} />}

            {/* Workflow buttons */}
            {currentId && (
              <div className="mt-4 bg-[#16161C] border border-[#2D2D35] rounded-md p-4 flex flex-wrap items-center gap-2">
                <div className="text-xs uppercase tracking-widest text-zinc-500">Workflow</div>
                <Step ok={["VERIFIED","APPROVED","PAID"].includes(currentStatus)} label="Draft" />
                <Step ok={["VERIFIED","APPROVED","PAID"].includes(currentStatus)} label="Verified" />
                <Step ok={["APPROVED","PAID"].includes(currentStatus)} label="Approved" />
                <Step ok={currentStatus === "PAID"} label="Paid" />
                <div className="ml-auto flex gap-2 flex-wrap">
                  {currentStatus === "DRAFT" && <Button size="sm" onClick={()=>changeStatus("VERIFIED")} data-testid="mp-verify" className="bg-[#E50914] hover:bg-[#C80812]">Verify</Button>}
                  {currentStatus === "VERIFIED" && <Button size="sm" onClick={()=>changeStatus("APPROVED")} data-testid="mp-approve" className="bg-[#22C55E]/15 text-[#22C55E] hover:bg-[#22C55E]/25 border border-[#22C55E]/30">Approve</Button>}
                  {currentStatus === "APPROVED" && <Button size="sm" onClick={()=>changeStatus("PAID")} data-testid="mp-mark-paid" className="bg-[#22C55E] hover:bg-[#16A34A] text-white">Mark Paid</Button>}
                  <Button size="sm" variant="outline" onClick={()=>downloadPDF(currentId)} className="border-[#2D2D35] bg-transparent" data-testid="mp-pdf">
                    <DownloadSimple size={14} className="mr-1" /> Salary Slip
                  </Button>
                  {(user?.role === "super_admin" || user?.role === "managing_director") && (
                    <Button size="sm" variant="outline" onClick={()=>downloadDetailedPDF(currentId)}
                            className="border-[#E50914]/40 text-[#E50914] bg-transparent hover:bg-[#E50914]/10"
                            data-testid="mp-detailed-pdf">
                      <DownloadSimple size={14} className="mr-1" /> Detailed Report
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={()=>window.print()} className="border-[#2D2D35] bg-transparent" data-testid="mp-print">
                    Print
                  </Button>
                  <Button size="sm" variant="outline" onClick={archiveCurrent} className="border-[#FF3B30]/40 text-[#FF3B30] bg-transparent hover:bg-[#FF3B30]/10">
                    <Archive size={14} className="mr-1" /> Archive
                  </Button>
                </div>
              </div>
            )}
          </>)}
        </TabsContent>

        <TabsContent value="list" className="mt-5">
          <SlipsTable rows={slips} onPDF={downloadPDF} onLoad={loadSlip} />
        </TabsContent>

        <TabsContent value="archive" className="mt-5">
          <SlipsTable rows={archivedSlips} onPDF={downloadPDF} onRestore={restore} />
        </TabsContent>
      </Tabs>

      {/* Deduction dialog */}
      <Dialog open={showDed} onOpenChange={setShowDed}>
        <DialogContent className="bg-[#16161C] border-[#2D2D35] text-white">
          <DialogHeader><DialogTitle>Add Deduction</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Category</Label>
              <Select value={dedForm.category} onValueChange={(v)=>setDedForm({...dedForm, category: v})}>
                <SelectTrigger className="mt-2 bg-[#0D0D10] border-[#2D2D35]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white max-h-72">
                  {DEDUCTION_CATS.map((c) => <SelectItem key={c} value={c}>{c.replaceAll("_"," ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Reason</Label>
              <Input value={dedForm.reason} onChange={(e)=>setDedForm({...dedForm, reason: e.target.value})}
                     className="mt-2 bg-[#0D0D10] border-[#2D2D35]" /></div>
            <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Amount (₹)</Label>
              <Input type="number" value={dedForm.amount} onChange={(e)=>setDedForm({...dedForm, amount: Number(e.target.value)})}
                     className="mt-2 bg-[#0D0D10] border-[#2D2D35]" data-testid="mp-ded-amount" /></div>
            <Button onClick={()=>{ setDeductions([...deductions, dedForm]); setShowDed(false); setDedForm({...dedForm, reason:"", amount:0}); }}
                    className="w-full bg-[#E50914] hover:bg-[#C80812]" data-testid="mp-ded-save">Add</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bonus dialog */}
      <Dialog open={showBon} onOpenChange={setShowBon}>
        <DialogContent className="bg-[#16161C] border-[#2D2D35] text-white">
          <DialogHeader><DialogTitle>Add Bonus / Incentive</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Type</Label>
              <Select value={bonForm.bonus_type} onValueChange={(v)=>setBonForm({...bonForm, bonus_type: v})}>
                <SelectTrigger className="mt-2 bg-[#0D0D10] border-[#2D2D35]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white max-h-72">
                  {BONUS_TYPES.map((c) => <SelectItem key={c} value={c}>{c.replaceAll("_"," ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Reason</Label>
              <Input value={bonForm.reason} onChange={(e)=>setBonForm({...bonForm, reason: e.target.value})}
                     className="mt-2 bg-[#0D0D10] border-[#2D2D35]" /></div>
            <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Amount (₹)</Label>
              <Input type="number" value={bonForm.amount} onChange={(e)=>setBonForm({...bonForm, amount: Number(e.target.value)})}
                     className="mt-2 bg-[#0D0D10] border-[#2D2D35]" data-testid="mp-bon-amount" /></div>
            <Button onClick={()=>{ setBonuses([...bonuses, bonForm]); setShowBon(false); setBonForm({...bonForm, reason:"", amount:0}); }}
                    className="w-full bg-[#E50914] hover:bg-[#C80812]" data-testid="mp-bon-save">Add</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Block({ title, total, addLabel, onAdd, children, testid }) {
  return (
    <div className="bg-[#16161C] border border-[#2D2D35] rounded-md">
      <div className="px-4 py-3 border-b border-[#2D2D35] flex items-center justify-between">
        <div className="text-sm font-medium">{title} <span className="text-xs text-zinc-500 ml-2 font-mono">₹ {Number(total||0).toLocaleString()}</span></div>
        <Button size="sm" variant="outline" onClick={onAdd} data-testid={testid} className="border-[#2D2D35] bg-transparent">
          <Plus size={14} className="mr-1" /> {addLabel}
        </Button>
      </div>
      <div className="divide-y divide-[#2D2D35]/60">{children}</div>
    </div>
  );
}
function Row({ left, right, color, onRemove }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5 text-xs">
      <span>{left}</span>
      <div className="flex items-center gap-3">
        <span className="font-mono" style={{ color }}>{right}</span>
        <button onClick={onRemove} className="text-zinc-500 hover:text-[#FF3B30]"><Trash size={14} /></button>
      </div>
    </div>
  );
}
function Empty({ children }) { return <div className="px-4 py-4 text-xs text-zinc-500 text-center">{children}</div>; }

function FinalBuilder({ calc, deductions = [], bonuses = [] }) {
  const sections = [
    {
      title: `Payroll Summary · Method: ${(calc.salary_calc_method || "FULL_DAY").replaceAll("_", " ")}`,
      cells: [
        ["Expected Working Days", calc.total_days, ""],
        ["Present Days", calc.present_days, "good"],
        ["Half Days", calc.half_days, "warn"],
        ["Paid Leaves", calc.paid_leave_days, ""],
        ["Holidays / Weekly Off", calc.holiday_days, ""],
        ["Absent / Unpaid", calc.unpaid_days, "bad"],
        ["Auto-Absent (no time)", calc.auto_absent_days || 0, "bad"],
      ],
    },
    {
      title: "Working Hours",
      cells: [
        ["Expected Hours", calc.expected_hours, ""],
        ["Actual Hours Worked", calc.worked_hours, "good"],
        ["Missing Hours", calc.missing_hours ?? calc.shortfall_hours ?? 0, "bad"],
        ["Overtime Hours", calc.overtime_hours, "warn"],
        ["Hourly Rate", `₹ ${calc.hourly_rate}`, ""],
      ],
    },
  ];
  if (calc.performance_enabled) {
    sections.push({
      title: "Performance Bonus & Deduction",
      cells: [
        ["Daily Assigned", calc.daily_assigned_jobs, ""],
        ["Minimum Required", calc.min_required_jobs, ""],
        ["Total Assigned Target", calc.total_perf_target_jobs, ""],
        ["Total Jobs Completed", calc.total_jobs_completed, "good"],
        ["Total Missing Jobs", calc.total_perf_missing_jobs, "bad"],
        ["Total Extra Jobs", calc.total_perf_extra_jobs, "good"],
        ["Performance Deduction", `- ₹ ${Number(calc.total_perf_deduction || 0).toLocaleString()}`, "bad"],
        ["Performance Bonus", `+ ₹ ${Number(calc.total_perf_bonus || 0).toLocaleString()}`, "earn"],
        ["Performance %", `${calc.performance_pct}%`, calc.performance_pct >= 100 ? "good" : "warn"],
      ],
    });
  }
  if (calc.productivity_enabled) {
    sections.push({
      title: "Productivity (Legacy)",
      cells: [
        ["Jobs Assigned", calc.total_jobs_assigned, ""],
        ["Jobs Completed", calc.total_jobs_completed, "good"],
        ["Target Jobs", calc.total_target_jobs, ""],
        ["Extra Jobs", calc.total_extra_jobs, "good"],
        ["Achievement %", `${calc.achievement_pct}%`, calc.achievement_pct >= 100 ? "good" : "warn"],
        ["Productivity Incentive", `₹ ${calc.productivity_incentive.toLocaleString()}`, "earn"],
      ],
    });
  }
  const isWH = calc.salary_calc_method === "WORKED_HOURS";
  sections.push({
    title: "Salary Calculation",
    cells: [
      ["Monthly Salary", `₹ ${Number(calc.monthly_salary).toLocaleString()}`, ""],
      ["Daily Rate", `₹ ${calc.daily_rate}`, ""],
      ["Hourly Rate", `₹ ${calc.hourly_rate}`, ""],
      [isWH ? "Worked Hours Salary" : "Attendance Salary",
       `₹ ${calc.attendance_salary.toLocaleString()}`, "earn"],
      ["Overtime Amount", `₹ ${calc.overtime_amount.toLocaleString()}`, "earn"],
      ["Performance Bonus", `₹ ${(calc.total_perf_bonus || 0).toLocaleString()}`, "earn"],
      ["Productivity Incentive", `₹ ${(calc.productivity_incentive || 0).toLocaleString()}`, "earn"],
      ["Bonus Total", `₹ ${calc.bonus_total.toLocaleString()}`, "earn"],
      ["Performance Deduction", `- ₹ ${(calc.total_perf_deduction || 0).toLocaleString()}`, "bad"],
    ],
  });
  // Deductions breakdown by category
  const dedSection = { title: "Deductions Breakdown", cells: [] };
  if (deductions.length) {
    deductions.forEach((d) => dedSection.cells.push([
      (d.category || "Deduction").replaceAll("_", " "),
      `₹ ${Number(d.amount || 0).toLocaleString()}`, "bad",
    ]));
  } else {
    dedSection.cells.push(["No deductions added", "₹ 0", ""]);
  }
  dedSection.cells.push(["TOTAL DEDUCTIONS", `₹ ${calc.deduction_total.toLocaleString()}`, "bad"]);
  sections.push(dedSection);

  // ---- Headline equation (defense-in-depth: math must match the net banner)
  const totalEarn = (calc.attendance_salary || 0)
                  + (calc.overtime_amount || 0)
                  + (calc.bonus_total || 0)
                  + (calc.productivity_incentive || 0)
                  + (calc.total_perf_bonus || 0);
  const totalDed  = (calc.deduction_total || 0) + (calc.total_perf_deduction || 0);
  const computedNet = Math.round((totalEarn - totalDed) * 100) / 100;

  const colorClass = (k) => k === "good" ? "text-[#22C55E]" :
                            k === "warn" ? "text-[#F59E0B]" :
                            k === "bad"  ? "text-[#FF3B30]" :
                            k === "earn" ? "text-[#22C55E]" : "text-white";
  const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <div className="mt-4 space-y-4" data-testid="mp-final-builder">
      {sections.map((s, si) => (
        <div key={si} className="bg-[#16161C] border border-[#2D2D35] rounded-md p-5">
          <div className="text-sm font-medium tracking-wide mb-3 inline-flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#E50914]">{si + 1}</span>
            {s.title}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {s.cells.map((c, i) => (
              <div key={i} className="bg-[#0D0D10] border border-[#2D2D35] rounded p-3">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">{c[0]}</div>
                <div className={`font-mono text-lg mt-1 ${colorClass(c[2])}`}>{c[1]}</div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Net = Total Earnings − Total Deductions (visible equation) */}
      <div className="bg-[#0D0D10] border border-[#2D2D35] rounded-md p-4 text-xs font-mono flex flex-wrap items-center gap-x-3 gap-y-1"
           data-testid="mp-net-equation">
        <span className="text-zinc-500">Net =</span>
        <span className="text-[#22C55E]">Earnings (A) ₹{fmt(totalEarn)}</span>
        <span className="text-zinc-500">−</span>
        <span className="text-[#FF3B30]">Deductions (B) ₹{fmt(totalDed)}</span>
        <span className="text-zinc-500">=</span>
        <span className="text-[#E50914] font-semibold text-base">₹ {fmt(computedNet)}</span>
        {Math.abs(computedNet - (calc.net_salary || 0)) > 0.01 && (
          <span className="ml-3 text-[10px] uppercase tracking-widest text-[#F59E0B]">
            stored net ₹{fmt(calc.net_salary)} — re-save to refresh
          </span>
        )}
      </div>

      <div className="mt-2 bg-[#0D0D10] border border-[#E50914]/50 rounded-md p-5 flex items-center justify-between"
           data-testid="mp-net-banner">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-zinc-500">Final</div>
          <div className="text-sm uppercase tracking-[0.2em] text-zinc-300">Net Salary Payable</div>
        </div>
        <div className="font-mono text-4xl font-semibold text-[#E50914]" data-testid="mp-net-salary">
          ₹ {fmt(computedNet)}
        </div>
      </div>
    </div>
  );
}

function Step({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${ok ? "bg-[#22C55E]/15 text-[#22C55E]" : "bg-[#0D0D10] border border-[#2D2D35] text-zinc-500"}`}>
      <CheckCircle size={12} weight={ok ? "fill" : "regular"} /> {label}
    </span>
  );
}

function SlipsTable({ rows, onPDF, onLoad, onRestore }) {
  return (
    <div className="bg-[#16161C] border border-[#2D2D35] rounded-md overflow-hidden">
      <table className="w-full" data-testid="mp-slips-table">
        <thead>
          <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[#2D2D35]">
            <th className="text-left p-3">Employee</th>
            <th className="text-left p-3">Period</th>
            <th className="text-right p-3">Net</th>
            <th className="text-right p-3">Status</th>
            <th className="text-right p-3"></th>
          </tr>
        </thead>
        <tbody className="text-sm">
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-[#2D2D35]/60 hover:bg-[#1C1C24]">
              <td className="p-3">{r.employee_name} <span className="text-[10px] text-zinc-500 font-mono ml-1">{r.employee_code}</span></td>
              <td className="p-3 font-mono text-xs">{String(r.month).padStart(2,"0")}/{r.year}</td>
              <td className="p-3 text-right font-mono text-[#E50914]">₹ {Number(r.calculation?.net_salary || 0).toLocaleString()}</td>
              <td className="p-3 text-right text-xs"><span className="px-2 py-1 rounded bg-[#0D0D10] border border-[#2D2D35]">{r.status}</span></td>
              <td className="p-3 text-right space-x-1">
                {onLoad && <button onClick={()=>onLoad(r)} className="text-xs text-zinc-400 hover:text-[#E50914] px-2">Open</button>}
                <button onClick={()=>onPDF(r.id)} className="text-zinc-400 hover:text-[#E50914] p-2 inline-block"><DownloadSimple size={14} /></button>
                {onRestore && <button onClick={()=>onRestore(r.id)} className="text-xs text-zinc-400 hover:text-[#22C55E] px-2">Restore</button>}
              </td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan="5" className="text-center text-zinc-500 p-8 text-sm">Nothing here.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
